import { useLocalStorage } from 'usehooks-ts'
import { RegionMode } from '@npm-client-frontends/shared-constants'
import { useUserCognitoGroups } from './useUserCognitoGroups'
import { useEffect, useMemo } from 'react'
import {
  CognitoGroups,
  AudienceGroups,
} from '@npm-client-frontends/client-frontends-shared-types'
import {
  Channels,
  dispatch,
  Events,
  listen,
  Sources,
} from '@npm-client-frontends/client-frontends-custom-events'

const LOCAL_STORAGE_KEY_CLAIMS_TOGGLE_STATUS = 'claims-toggle-status'

export type ClaimsTogglePreference = {
  selectedClaims: AudienceGroups[]
  allClaimsSelected: boolean
  toggleClaims: (claim: AudienceGroups) => void
  setClaims: (claims: AudienceGroups[]) => void
  selectedRegionMode: RegionMode | null
  globalRegionMode: RegionMode | null
}

export const useClaimsToggle = (): ClaimsTogglePreference => {
  const { userAudienceGroups } = useUserCognitoGroups()

  const [
    statusLocal = [
      CognitoGroups.ContentUtilityScale,
      CognitoGroups.ContentDistributedGeneration,
      CognitoGroups.ContentEurope,
      CognitoGroups.ContentDataCenters,
    ],
    setStatus,
  ] = useLocalStorage<AudienceGroups[]>(
    LOCAL_STORAGE_KEY_CLAIMS_TOGGLE_STATUS,
    [
      CognitoGroups.ContentUtilityScale,
      CognitoGroups.ContentDistributedGeneration,
      CognitoGroups.ContentEurope,
      CognitoGroups.ContentDataCenters,
    ],
  )

  useEffect(() => {
    const validSelectedClaims = statusLocal.filter((s) =>
      userAudienceGroups.includes(s as AudienceGroups),
    )
    setStatus(
      validSelectedClaims.length > 0 ? validSelectedClaims : [...userAudienceGroups]
    )
  }, [userAudienceGroups])

  const status = useMemo(() => {
    const validSelectedClaims = statusLocal.filter((s) =>
      userAudienceGroups.includes(s as AudienceGroups),
    )
    return validSelectedClaims.length > 0
      ? validSelectedClaims
      : [...userAudienceGroups]
  }, [statusLocal, userAudienceGroups])

  const allClaimsSelected = useMemo(
    () => status.length === userAudienceGroups.length,
    [status],
  )

  const setStatusAndDispatch = (newStatus: AudienceGroups[]) => {
    setStatus(newStatus)
    dispatch(Channels.UI, Sources.NAV, Events.TOGGLE_CLAIM)
  }

  const setClaims = (claims: AudienceGroups[]) => {
    const validClaims = claims.filter((claim) =>
      userAudienceGroups.includes(claim as AudienceGroups),
    )
    setStatusAndDispatch(
      validClaims.length > 0 ? validClaims : [...userAudienceGroups],
    )
  }

  const toggleStatus = (claim: AudienceGroups) => {
    const selectedClaims = userAudienceGroups.reduce<AudienceGroups[]>(
      (acc, group) => {
        let groupIsSelected = status.includes(group)
        if (claim === group) groupIsSelected = !groupIsSelected
        if (groupIsSelected) acc.push(group)
        return acc
      },
      [],
    )

    setStatusAndDispatch(
      selectedClaims.length > 0 ? selectedClaims : [...userAudienceGroups],
    )
  }

  const determineRegionMode = (groups: AudienceGroups[]): RegionMode | null => {
    const hasUTorDGorDC =
      groups.includes(CognitoGroups.ContentUtilityScale) ||
      groups.includes(CognitoGroups.ContentDistributedGeneration) ||
      groups.includes(CognitoGroups.ContentDataCenters)
    const hasEUR = groups.includes(CognitoGroups.ContentEurope)

    if (hasUTorDGorDC && hasEUR) return RegionMode.MULTI
    if (hasUTorDGorDC) return RegionMode.US
    if (hasEUR) return RegionMode.EUR
    return null
  }

  const selectedRegionMode = useMemo(
    () => determineRegionMode(status),
    [status],
  )

  const globalRegionMode = useMemo(
    () => determineRegionMode(userAudienceGroups),
    [userAudienceGroups],
  )

  useEffect(() => {
    const removeListener = listen(Channels.UI, (event) => {
      if (event.detail.source === Sources.LEGACY) {
        const selectedClaim = event.detail.data as CognitoGroups
        setStatusAndDispatch([selectedClaim])
      }
    })

    return () => {
      removeListener()
    }
  }, [])

  return {
    selectedClaims: status,
    allClaimsSelected,
    toggleClaims: toggleStatus,
    setClaims,
    selectedRegionMode,
    globalRegionMode,
  }
}
